// src/pages/public/BusinessPage.jsx
import React, { useState, useEffect, useRef } from 'react';
import {
  ChevronDown, Check, Star, HeartHandshake, TrendingUp,
  Headphones, Award, Settings, ArrowRight, MessageCircle,
} from 'lucide-react';
import MainLayout from '../../components/layout/MainLayout';
import { getAllBusinessPackages } from '../../services/business';

/* ── Color tokens ── */
const C = {
  amber:      '#d97706',
  amberLight: '#fef3c7',
  amberDark:  '#92400e',
  amberHover: '#b45309',
  white:      '#ffffff',
  gray50:     '#f9fafb',
  gray100:    '#f3f4f6',
  gray200:    '#e5e7eb',
  gray400:    '#9ca3af',
  gray500:    '#6b7280',
  gray600:    '#4b5563',
  gray700:    '#374151',
  gray900:    '#111827',
  stone50:    '#fafaf9',
  green50:    '#f0fdf4',
  green100:   '#dcfce7',
  green600:   '#16a34a',
  green700:   '#15803d',
  red100:     '#fee2e2',
  red800:     '#991b1b',
};

/* ── WhatsApp SVG ── */
const WaIcon = ({ size = 18 }) => (
  <svg width={size} height={size} fill="currentColor" viewBox="0 0 24 24">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
  </svg>
);

/* ── useInView ── */
const useInView = (threshold = 0.12) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  const triggered = useRef(false);
  useEffect(() => {
    if (triggered.current) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && !triggered.current) {
        triggered.current = true;
        setInView(true);
        obs.disconnect();
      }
    }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, inView];
};

/* ── Anim ── */
const Anim = ({ children, delay = 0, dir = 'up', style = {} }) => {
  const [ref, inView] = useInView();
  const from = { up: 'translateY(50px)', left: 'translateX(-50px)', right: 'translateX(50px)', scale: 'scale(0.88)' };
  return (
    <div ref={ref} style={{
      opacity: inView ? 1 : 0,
      transform: inView ? 'none' : from[dir],
      transition: `opacity 0.8s cubic-bezier(0.16,1,0.3,1) ${delay}ms, transform 0.8s cubic-bezier(0.16,1,0.3,1) ${delay}ms`,
      ...style,
    }}>
      {children}
    </div>
  );
};

/* ── Orb ── */
const Orb = ({ size, color, top, left, delay = 0 }) => (
  <div style={{
    position: 'absolute', width: size, height: size, borderRadius: '50%',
    background: color, top, left, filter: 'blur(70px)', opacity: 0.28,
    animation: `floatOrb ${7 + delay}s ease-in-out infinite alternate`,
    animationDelay: `${delay}s`, pointerEvents: 'none', zIndex: 0,
  }} />
);

/* ── SectionLabel ── */
const SectionLabel = ({ text }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'center', marginBottom: 18 }}>
    <div style={{ width: 40, height: 3, background: `linear-gradient(90deg, ${C.amber}, #f59e0b)`, borderRadius: 2 }} />
    <span style={{ fontSize: 12, color: C.amber, fontWeight: 700, letterSpacing: '0.15em', textTransform: 'uppercase' }}>{text}</span>
    <div style={{ width: 40, height: 3, background: `linear-gradient(90deg, #f59e0b, ${C.amber})`, borderRadius: 2 }} />
  </div>
);

/* ── WaBtn ── */
const WaBtn = ({ onClick, children, style = {} }) => {
  const [hov, setHov] = useState(false);
  return (
    <button onClick={onClick}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        padding: '13px 26px', borderRadius: 50, border: 'none', cursor: 'pointer',
        backgroundColor: hov ? C.green700 : C.green600, color: '#fff',
        fontWeight: 700, fontSize: 15, fontFamily: 'inherit',
        transform: hov ? 'translateY(-2px)' : 'translateY(0)',
        boxShadow: hov ? '0 14px 32px rgba(22,163,74,0.42)' : '0 4px 16px rgba(22,163,74,0.28)',
        transition: 'all 0.3s cubic-bezier(0.34,1.56,0.64,1)', ...style,
      }}
    >
      {children}
    </button>
  );
};

/* ── AmberBtn ── */
const AmberBtn = ({ onClick, children, style = {} }) => {
  const [hov, setHov] = useState(false);
  return (
    <button onClick={onClick}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        padding: '13px 26px', borderRadius: 50, border: 'none', cursor: 'pointer',
        backgroundColor: hov ? C.amberHover : C.amber, color: '#fff',
        fontWeight: 700, fontSize: 15, fontFamily: 'inherit',
        transform: hov ? 'translateY(-2px)' : 'translateY(0)',
        boxShadow: hov ? '0 14px 32px rgba(217,119,6,0.42)' : '0 4px 16px rgba(217,119,6,0.28)',
        transition: 'all 0.3s cubic-bezier(0.34,1.56,0.64,1)', ...style,
      }}
    >
      {children}
    </button>
  );
};

/* ── OutlineBtn (dark bg) ── */
const OutlineBtn = ({ onClick, children, style = {} }) => {
  const [hov, setHov] = useState(false);
  return (
    <button onClick={onClick}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        padding: '13px 26px', borderRadius: 50, cursor: 'pointer',
        backgroundColor: hov ? 'rgba(255,255,255,0.12)' : 'transparent',
        color: '#fff', border: '2px solid rgba(255,255,255,0.55)',
        fontWeight: 700, fontSize: 15, fontFamily: 'inherit',
        transform: hov ? 'translateY(-2px)' : 'translateY(0)',
        transition: 'all 0.3s cubic-bezier(0.34,1.56,0.64,1)', ...style,
      }}
    >
      {children}
    </button>
  );
};

/* ── BenefitCard ── */
const BenefitCard = ({ iconEl, iconBg, title, desc, delay }) => {
  const [hov, setHov] = useState(false);
  return (
    <Anim delay={delay}>
      <div onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)} style={{
        background: hov ? 'rgba(217,119,6,0.04)' : C.white,
        borderRadius: 22, padding: '36px 28px', textAlign: 'center', cursor: 'default',
        boxShadow: hov ? '0 24px 60px rgba(0,0,0,0.1)' : '0 4px 20px rgba(0,0,0,0.06)',
        border: `1.5px solid ${hov ? 'rgba(217,119,6,0.22)' : C.gray100}`,
        transform: hov ? 'translateY(-10px)' : 'translateY(0)',
        transition: 'all 0.45s cubic-bezier(0.34,1.56,0.64,1)',
      }}>
        <div style={{
          width: 72, height: 72, borderRadius: 20, backgroundColor: iconBg,
          display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px',
          transform: hov ? 'scale(1.2) rotate(-8deg)' : 'scale(1)',
          transition: 'transform 0.4s cubic-bezier(0.34,1.56,0.64,1)',
        }}>
          {iconEl}
        </div>
        <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 20, fontWeight: 700, color: C.gray900, marginBottom: 10 }}>{title}</h3>
        <p style={{ fontSize: 13.5, color: C.gray500, lineHeight: 1.78 }}>{desc}</p>
      </div>
    </Anim>
  );
};

/* ── PackageCard (featured + regular) ── */
const PkgCard = ({ pkg, featured, getTypeLabel, normalizeFeatures, onDetail, onWhatsApp }) => {
  const [hov, setHov] = useState(false);
  const features = normalizeFeatures(pkg.features);
  const fee = parseFloat(pkg.management_fee_percentage);

  return (
    <Anim delay={0}>
      <div onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)} style={{
        position: 'relative', background: featured ? 'linear-gradient(135deg, #fffbeb, #fff7ed)' : C.white,
        borderRadius: 24, padding: featured ? '40px 32px 32px' : '32px',
        border: `${featured ? 3 : 1.5}px solid ${hov ? C.amber : (featured ? 'rgba(217,119,6,0.35)' : C.gray200)}`,
        boxShadow: hov ? '0 32px 70px rgba(0,0,0,0.13)' : '0 6px 24px rgba(0,0,0,0.07)',
        transform: hov ? 'translateY(-10px)' : 'translateY(0)',
        transition: 'all 0.45s cubic-bezier(0.34,1.56,0.64,1)',
        display: 'flex', flexDirection: 'column', height: '100%',
      }}>
        {featured && (
          <div style={{
            position: 'absolute', top: -18, left: '50%', transform: 'translateX(-50%)',
            background: C.amber, color: '#fff', padding: '6px 20px', borderRadius: 50,
            fontSize: 12, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
            boxShadow: '0 4px 14px rgba(217,119,6,0.4)', display: 'flex', alignItems: 'center', gap: 6,
          }}>
            <Star size={12} fill="#fff" color="#fff" /> Featured
          </div>
        )}

        <div style={{ flex: 1 }}>
          <div style={{ textAlign: 'center', marginBottom: 20 }}>
            <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 24, fontWeight: 700, color: C.gray900, marginBottom: 10 }}>{pkg.name}</h3>
            <span style={{ display: 'inline-block', padding: '5px 16px', borderRadius: 50, fontSize: 12.5, fontWeight: 700, backgroundColor: featured ? C.amberLight : C.gray100, color: featured ? C.amberDark : C.gray700 }}>
              {getTypeLabel(pkg.package_type)}
            </span>
          </div>

          <p style={{ fontSize: 14, color: C.gray600, lineHeight: 1.75, textAlign: 'center', marginBottom: 20 }}>{pkg.description}</p>

          <div style={{ textAlign: 'center', background: C.gray50, borderRadius: 16, padding: '20px 16px', marginBottom: 22 }}>
            {fee > 0 ? (
              <>
                <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 44, fontWeight: 700, color: C.amber, lineHeight: 1 }}>{pkg.management_fee_percentage}%</div>
                <div style={{ fontSize: 13, color: C.gray500, marginTop: 6 }}>Management Fee</div>
              </>
            ) : (
              <>
                <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, fontWeight: 700, color: C.amber }}>Special Management Fee</div>
                <div style={{ fontSize: 13, color: C.gray500, marginTop: 6 }}>Contact us for details</div>
              </>
            )}
          </div>

          <div style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 13.5, fontWeight: 700, color: C.gray900, textAlign: 'center', marginBottom: 14 }}>Key Features:</div>
            {features.slice(0, 4).map((f, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10 }}>
                <div style={{ width: 20, height: 20, borderRadius: '50%', backgroundColor: C.amberLight, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                  <Check size={11} color={C.amber} strokeWidth={3} />
                </div>
                <span style={{ fontSize: 13.5, color: C.gray700, lineHeight: 1.55 }}>{f}</span>
              </div>
            ))}
            {features.length > 4 && (
              <div style={{ textAlign: 'center', fontSize: 13, color: C.amber, fontWeight: 700, marginTop: 6 }}>
                +{features.length - 4} more features
              </div>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <AmberBtn onClick={() => onDetail(pkg.id)} style={{ borderRadius: 12, padding: '12px' }}>
            View Package Details
          </AmberBtn>
          <WaBtn onClick={() => onWhatsApp(pkg)} style={{ borderRadius: 12, padding: '12px' }}>
            <WaIcon size={17} /> Consultation
          </WaBtn>
        </div>
      </div>
    </Anim>
  );
};

/* ── Feature Comparison Data ── */
const featureComparisonData = [
  { category: 'MANAGEMENT FEATURES', features: [
    { name: 'Dedicated Villa Manager',       full_management: true,  marketing: false, operational: true  },
    { name: 'Staff Training and Development', full_management: true,  marketing: false, operational: true  },
  ]},
  { category: 'OPERATIONAL FEATURES', features: [
    { name: 'Guest Check-in/Check-out Handling', full_management: true,  marketing: false, operational: true  },
    { name: 'Complaint and Issue Resolution',     full_management: true,  marketing: false, operational: true  },
    { name: 'Booking Payment Collection',         full_management: true,  marketing: true,  operational: false },
    { name: 'Calendar Synchronization',           full_management: true,  marketing: true,  operational: false },
    { name: 'Reservation System Management',      full_management: true,  marketing: true,  operational: false },
  ]},
  { category: 'COMMUNICATION FEATURES', features: [
    { name: '24-hour Guest Communication',    full_management: true,  marketing: true,  operational: true  },
    { name: 'Personalized Concierge Services', full_management: true,  marketing: true,  operational: false },
    { name: 'Guest Mobile Application',        full_management: true,  marketing: true,  operational: false },
  ]},
  { category: 'MARKETING FEATURES', features: [
    { name: 'Global Listing Distribution (15+ platforms)', full_management: true, marketing: true, operational: false },
    { name: 'Listing Creation and Optimization',           full_management: true, marketing: true, operational: false },
    { name: 'Market Analysis',                             full_management: true, marketing: true, operational: false },
  ]},
  { category: 'PRICING FEATURES', features: [
    { name: 'Dynamic Pricing System', full_management: true, marketing: true, operational: false },
  ]},
  { category: 'REPORTING FEATURES', features: [
    { name: 'Monthly Revenue Report', full_management: true, marketing: true,  operational: false },
    { name: 'Monthly Cost Report',    full_management: true, marketing: false, operational: true  },
  ]},
  { category: 'MAINTENANCE FEATURES', features: [
    { name: 'Property Maintenance & Quality Assurance', full_management: true, marketing: false, operational: true },
    { name: 'Regular Property Inspection',              full_management: true, marketing: false, operational: true },
  ]},
];

/* ══════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════ */
const BusinessPage = () => {
  const [packages, setPackages]               = useState([]);
  const [featuredPackages, setFeaturedPackages] = useState([]);
  const [loading, setLoading]                 = useState(true);
  const [selectedType, setSelectedType]       = useState('all');
  const [scrollY, setScrollY]                 = useState(0);
  const [mousePos, setMousePos]               = useState({ x: 0, y: 0 });
  const videoRef = useRef(null);

  const heroOpacity  = Math.max(0, 1 - scrollY / 580);
  const heroParallax = scrollY * 0.35;

  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    const onMouse  = e  => setMousePos({ x: e.clientX, y: e.clientY });
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('mousemove', onMouse, { passive: true });
    return () => { window.removeEventListener('scroll', onScroll); window.removeEventListener('mousemove', onMouse); };
  }, []);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        setLoading(true);
        const res = await getAllBusinessPackages({ status: 'active', limit: 50 });
        let data = res?.data?.data || (Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []));
        setPackages(data);
        setFeaturedPackages(data.filter(p => p.featured === true));
      } catch (e) {
        console.error(e);
        setPackages([]);
        setFeaturedPackages([]);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  const packageTypes = [
    { value: 'all',             label: 'All Packages'       },
    { value: 'full_management', label: 'Full Management'    },
    { value: 'marketing',       label: 'Marketing & Booking' },
    { value: 'operational',     label: 'Operational'        },
  ];

  const getTypeLabel = (type) => ({
    full_management: 'Full Management',
    marketing:       'Marketing & Booking',
    operational:     'Operational',
  }[type] || type);

  const filteredPackages = selectedType === 'all'
    ? packages
    : packages.filter(p => p.package_type === selectedType);

  const normalizeFeatures = (features) => {
    if (!Array.isArray(features)) return [];
    const groups = {
      'Marketing & Promotion':       ['marketing online', 'promosi', 'digital marketing', 'social media', 'iklan', 'listing di platform'],
      'Booking Management':          ['booking management', 'reservasi', 'manajemen booking', 'calendar', 'availability'],
      'Customer Service':            ['customer service', 'guest communication', 'layanan pelanggan', 'komunikasi tamu', '24/7 support'],
      'Housekeeping & Maintenance':  ['housekeeping', 'cleaning', 'maintenance', 'kebersihan', 'perawatan'],
      'Financial Management':        ['financial reporting', 'laporan keuangan', 'revenue management', 'pricing strategy'],
      'Property Management':         ['property management', 'manajemen properti', 'operational management', 'villa management'],
    };
    const result = new Set();
    features.forEach(f => {
      const lower = f.toLowerCase();
      let matched = false;
      for (const [group, keywords] of Object.entries(groups)) {
        if (keywords.some(k => lower.includes(k))) { result.add(group); matched = true; break; }
      }
      if (!matched) result.add(f);
    });
    return Array.from(result);
  };

  const handleWhatsApp = (pkg = null) => {
    const phone = '6287785686777';
    let msg = `Hello! I'm interested in your villa management services.`;
    if (pkg) {
      msg += `\n\nPackage: ${pkg.name} (${getTypeLabel(pkg.package_type)})\nDescription: ${pkg.description}`;
      if (parseFloat(pkg.management_fee_percentage) > 0) msg += `\nManagement Fee: ${pkg.management_fee_percentage}%`;
      msg += `\n\nI would like more info about:\n- Complete service details\n- Onboarding process\n- Partnership contract\n- Implementation timeline`;
    } else {
      msg += `\n\nI'd like to consult about:\n- Available villa management packages\n- Income potential for my villa\n- Partnership process`;
    }
    msg += `\n\nThank you!`;
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, '_blank');
  };

  const scrollToDetail = (id) => {
    document.getElementById(`pkg-detail-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const benefits = [
    { iconEl: <HeartHandshake size={30} color={C.amber} />,   iconBg: C.amberLight, title: 'Professional Management', desc: 'Experienced team managing villa properties in Bali' },
    { iconEl: <TrendingUp    size={30} color="#2563eb" />,    iconBg: '#dbeafe',    title: 'Maximize Revenue',        desc: 'Pricing and marketing strategies to optimize your income' },
    { iconEl: <Headphones    size={30} color="#16a34a" />,    iconBg: '#dcfce7',    title: '24/7 Support',            desc: 'Customer service and maintenance support around the clock' },
    { iconEl: <Award         size={30} color="#7c3aed" />,    iconBg: '#ede9fe',    title: 'Quality Assured',         desc: 'High service standards for maximum guest satisfaction' },
  ];

  const pkgTypeIcon = { full_management: HeartHandshake, marketing: TrendingUp, operational: Settings };

  return (
    <MainLayout>
    <div style={{ minHeight: '100vh', backgroundColor: C.white, overflowX: 'hidden', fontFamily: "'DM Sans', sans-serif" }}>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;600;700&family=DM+Sans:wght@300;400;500;600&display=swap');
        *, *::before, *::after { box-sizing: border-box; }

        @keyframes floatOrb  { 0% { transform: translateY(0) scale(1); } 100% { transform: translateY(-40px) scale(1.1); } }
        @keyframes heroIn    { from { opacity: 0; transform: translateY(40px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes shimmer   { 0% { background-position: -200% center; } 100% { background-position: 200% center; } }
        @keyframes pulseDot  { 0%,100% { box-shadow: 0 0 0 0 rgba(217,119,6,0.6); } 50% { box-shadow: 0 0 0 10px rgba(217,119,6,0); } }
        @keyframes bounce    { 0%,100% { transform: translateY(0); opacity:1; } 50% { transform: translateY(10px); opacity:0.4; } }
        @keyframes spin      { to { transform: rotate(360deg); } }

        .bh-1 { animation: heroIn 1.1s cubic-bezier(0.16,1,0.3,1) 0.20s both; }
        .bh-2 { animation: heroIn 1.1s cubic-bezier(0.16,1,0.3,1) 0.45s both; }
        .bh-3 { animation: heroIn 1.1s cubic-bezier(0.16,1,0.3,1) 0.70s both; }
        .bh-4 { animation: heroIn 1.1s cubic-bezier(0.16,1,0.3,1) 0.90s both; }

        .shimmer-text {
          background: linear-gradient(90deg, #d97706, #f59e0b, #d97706);
          background-size: 200% auto;
          -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
          animation: shimmer 3s linear infinite;
        }
        .scroll-bounce { animation: bounce 2s ease-in-out infinite; }

        .tab-btn { transition: all 0.3s cubic-bezier(0.34,1.56,0.64,1); }

        .tbl-row { transition: background 0.2s ease; }
        .tbl-row:hover { background: rgba(251,191,36,0.05); }

        .cursor-glow {
          pointer-events: none; position: fixed; width: 280px; height: 280px; border-radius: 50%;
          background: radial-gradient(circle, rgba(217,119,6,0.06) 0%, transparent 70%);
          transform: translate(-50%,-50%);
          transition: left 0.12s ease, top 0.12s ease;
          z-index: 9999; mix-blend-mode: multiply;
        }

        .detail-card { transition: box-shadow 0.4s ease; }
        .detail-card:hover { box-shadow: 0 32px 80px rgba(0,0,0,0.12) !important; }
      `}</style>

      {/* Cursor glow */}
      <div className="cursor-glow" style={{ left: mousePos.x, top: mousePos.y }} />

      

      {/* ══ HERO ══ */}
      <section style={{ position: 'relative', height: '100vh', minHeight: 580, overflow: 'hidden' }}>
        <video ref={videoRef} autoPlay muted loop playsInline style={{
          width: '100%', height: '100%', objectFit: 'cover',
          transform: `translateY(${heroParallax}px)`, willChange: 'transform',
        }}>
          <source src="/images/hero-video.mp4" type="video/mp4" />
          <img src="https://images.unsplash.com/photo-1582268611958-ebfd161ef9cf?w=1920&q=80" alt="Villa" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        </video>

        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(0,0,0,0.12), rgba(0,0,0,0.5), rgba(0,0,0,0.74))' }} />
        <Orb size="380px" color="radial-gradient(circle, #d97706, #f59e0b)" top="6%"  left="68%" delay={0} />
        <Orb size="240px" color="radial-gradient(circle, #92400e, #d97706)" top="60%" left="-3%" delay={2} />

        <div style={{
          position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '0 24px',
          transform: `translateY(${heroParallax * 0.4}px)`, opacity: heroOpacity,
        }}>
          <div className="bh-1" style={{ marginBottom: 24 }}>
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 10,
              padding: '10px 22px', backgroundColor: 'rgba(255,255,255,0.12)',
              backdropFilter: 'blur(12px)', border: '1px solid rgba(255,255,255,0.25)',
              borderRadius: 50, color: '#fff', fontSize: 12, fontWeight: 700,
              letterSpacing: '0.14em', textTransform: 'uppercase',
            }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: C.amber, animation: 'pulseDot 2s ease-in-out infinite' }} />
              Premium Villa Management Services
            </span>
          </div>

          <h1 className="bh-2" style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: 'clamp(36px,8vw,80px)', fontWeight: 700,
            color: '#fff', lineHeight: 1.06, letterSpacing: '-0.02em', marginBottom: 18,
          }}>
            Best Villa Management<br /><span className="shimmer-text">Packages in Bali</span>
          </h1>

          <p className="bh-3" style={{ fontSize: 'clamp(15px,2vw,19px)', color: 'rgba(255,255,255,0.82)', maxWidth: 580, marginBottom: 42, fontWeight: 300, lineHeight: 1.72 }}>
            Entrust your villa to us and get maximum passive income with professional management and the best service
          </p>

          <div className="bh-4" style={{ display: 'flex', gap: 14, flexWrap: 'wrap', justifyContent: 'center' }}>
            <WaBtn onClick={() => handleWhatsApp()} style={{ padding: '14px 30px', fontSize: 16 }}>
              <WaIcon size={18} /> Chat WhatsApp
            </WaBtn>
            <OutlineBtn onClick={() => document.getElementById('packages')?.scrollIntoView({ behavior: 'smooth' })} style={{ padding: '13px 30px', fontSize: 16 }}>
              View Packages <ArrowRight size={17} />
            </OutlineBtn>
          </div>
        </div>

        <div className="scroll-bounce" style={{ position: 'absolute', bottom: 34, left: '50%', transform: 'translateX(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, color: 'rgba(255,255,255,0.55)' }}>
          <span style={{ fontSize: 11, letterSpacing: '0.15em', textTransform: 'uppercase' }}>Scroll</span>
          <ChevronDown size={18} />
        </div>
      </section>

      {/* ══ BENEFITS ══ */}
      <section style={{ padding: '100px 24px', backgroundColor: C.white, position: 'relative', overflow: 'hidden' }}>
        <Orb size="460px" color="radial-gradient(circle, rgba(251,191,36,0.07), transparent)" top="-5%" left="-6%" delay={3} />
        <div style={{ maxWidth: 1100, margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <Anim delay={0}>
            <div style={{ textAlign: 'center', marginBottom: 60 }}>
              <SectionLabel text="Why Choose Us" />
              <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(28px,5vw,50px)', fontWeight: 700, color: C.gray900, marginBottom: 12 }}>
                Why Choose Our Services?
              </h2>
              <p style={{ fontSize: 16.5, color: C.gray500, maxWidth: 580, margin: '0 auto', lineHeight: 1.78 }}>
                We provide complete solutions to manage your villa professionally and transparently
              </p>
            </div>
          </Anim>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px,1fr))', gap: 22 }}>
            {benefits.map((b, i) => <BenefitCard key={i} delay={i * 90} {...b} />)}
          </div>
        </div>
      </section>

      {/* ══ FEATURED PACKAGES ══ */}
      {featuredPackages.length > 0 && (
        <section style={{ padding: '100px 24px', backgroundColor: C.stone50, position: 'relative', overflow: 'hidden' }}>
          <Orb size="400px" color="radial-gradient(circle, rgba(217,119,6,0.08), transparent)" top="0%" left="76%" delay={1} />
          <div style={{ maxWidth: 1100, margin: '0 auto', position: 'relative', zIndex: 1 }}>
            <Anim delay={0}>
              <div style={{ textAlign: 'center', marginBottom: 52 }}>
                <SectionLabel text="Most Popular" />
                <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(28px,5vw,50px)', fontWeight: 700, color: C.gray900 }}>
                  Featured Packages
                </h2>
              </div>
            </Anim>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px,1fr))', gap: 28 }}>
              {featuredPackages.map(pkg => (
                <PkgCard key={pkg.id} pkg={pkg} featured
                  getTypeLabel={getTypeLabel} normalizeFeatures={normalizeFeatures}
                  onDetail={scrollToDetail} onWhatsApp={handleWhatsApp}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ══ ALL PACKAGES ══ */}
      <section id="packages" style={{ padding: '100px 24px', backgroundColor: C.white, position: 'relative', overflow: 'hidden' }}>
        <Orb size="420px" color="radial-gradient(circle, rgba(251,191,36,0.07), transparent)" top="0%" left="-5%" delay={4} />
        <div style={{ maxWidth: 1100, margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <Anim delay={0}>
            <div style={{ textAlign: 'center', marginBottom: 52 }}>
              <SectionLabel text="All Packages" />
              <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(28px,5vw,50px)', fontWeight: 700, color: C.gray900, marginBottom: 12 }}>
                All Service Packages
              </h2>
              <p style={{ fontSize: 16.5, color: C.gray500, marginBottom: 36 }}>
                Choose the package that suits your villa needs
              </p>

              {/* Filter tabs */}
              <div style={{ display: 'inline-flex', background: C.white, border: `2px solid ${C.gray200}`, borderRadius: 50, padding: 5, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
                {packageTypes.map(t => (
                  <button key={t.value} className="tab-btn"
                    onClick={() => setSelectedType(t.value)}
                    style={{
                      padding: '10px 22px', borderRadius: 50, border: 'none', cursor: 'pointer',
                      backgroundColor: selectedType === t.value ? C.amber : 'transparent',
                      color: selectedType === t.value ? '#fff' : C.gray600,
                      fontWeight: 600, fontSize: 13.5, fontFamily: 'inherit',
                      boxShadow: selectedType === t.value ? '0 4px 14px rgba(217,119,6,0.38)' : 'none',
                    }}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
          </Anim>

          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
              <div style={{ width: 50, height: 50, border: `4px solid ${C.amberLight}`, borderTopColor: C.amber, borderRadius: '50%', animation: 'spin 0.9s linear infinite' }} />
            </div>
          ) : filteredPackages.length > 0 ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px,1fr))', gap: 28 }}>
              {filteredPackages.map(pkg => (
                <PkgCard key={pkg.id} pkg={pkg}
                  getTypeLabel={getTypeLabel} normalizeFeatures={normalizeFeatures}
                  onDetail={scrollToDetail} onWhatsApp={handleWhatsApp}
                />
              ))}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '60px 0', color: C.gray400, fontSize: 16 }}>
              No packages available for this category.
            </div>
          )}
        </div>
      </section>

      {/* ══ PACKAGE DETAILS ══ */}
      <section style={{ padding: '100px 24px', backgroundColor: C.stone50, position: 'relative', overflow: 'hidden' }}>
        <Orb size="380px" color="radial-gradient(circle, rgba(217,119,6,0.07), transparent)" top="10%" left="70%" delay={2} />
        <div style={{ maxWidth: 1100, margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <Anim delay={0}>
            <div style={{ textAlign: 'center', marginBottom: 52 }}>
              <SectionLabel text="Details" />
              <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(28px,5vw,50px)', fontWeight: 700, color: C.gray900 }}>
                Service Package Details
              </h2>
            </div>
          </Anim>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
            {packages.map((pkg, idx) => {
              const TypeIcon = pkgTypeIcon[pkg.pkg_type] || Settings;
              const fee = parseFloat(pkg.management_fee_percentage);
              const features = normalizeFeatures(pkg.features);
              const typeIconEl = {
                full_management: <HeartHandshake size={28} color={C.amber} />,
                marketing:       <TrendingUp     size={28} color={C.amber} />,
                operational:     <Settings       size={28} color={C.amber} />,
              }[pkg.package_type] || <Settings size={28} color={C.amber} />;

              return (
                <Anim key={pkg.id} delay={idx * 60}>
                  <div id={`pkg-detail-${pkg.id}`} className="detail-card" style={{
                    background: C.white, borderRadius: 24, padding: '40px',
                    border: `1.5px solid ${C.gray100}`,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.07)',
                    scrollMarginTop: 80,
                  }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px,1fr))', gap: 40, alignItems: 'start' }}>
                      {/* Left */}
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
                          <div style={{ width: 64, height: 64, borderRadius: 18, backgroundColor: C.amberLight, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            {typeIconEl}
                          </div>
                          <div>
                            <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 28, fontWeight: 700, color: C.gray900, marginBottom: 6 }}>{pkg.name}</h3>
                            <span style={{ display: 'inline-block', padding: '4px 14px', borderRadius: 50, fontSize: 12.5, fontWeight: 700, backgroundColor: C.amberLight, color: C.amberDark }}>
                              {getTypeLabel(pkg.package_type)}
                            </span>
                          </div>
                        </div>
                        <p style={{ fontSize: 14.5, color: C.gray600, lineHeight: 1.82, marginBottom: 22 }}>{pkg.description}</p>
                        <div style={{ background: C.gray50, borderRadius: 16, padding: '20px', marginBottom: 22 }}>
                          {fee > 0 ? (
                            <>
                              <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 42, fontWeight: 700, color: C.amber }}>{pkg.management_fee_percentage}%</div>
                              <div style={{ fontSize: 13, color: C.gray500, marginTop: 4 }}>Management Fee</div>
                            </>
                          ) : (
                            <>
                              <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, fontWeight: 700, color: C.amber }}>Special Management Fee</div>
                              <div style={{ fontSize: 13, color: C.gray500, marginTop: 4 }}>Contact us for details</div>
                            </>
                          )}
                        </div>
                        <WaBtn onClick={() => handleWhatsApp(pkg)} style={{ width: '100%', borderRadius: 14, padding: '14px' }}>
                          <WaIcon size={18} /> WhatsApp Consultation
                        </WaBtn>
                      </div>

                      {/* Right — features grid */}
                      <div>
                        <h4 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 20, fontWeight: 700, color: C.gray900, marginBottom: 18 }}>
                          Features & Services Included:
                        </h4>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px,1fr))', gap: 10 }}>
                          {features.map((f, i) => (
                            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, background: C.gray50, borderRadius: 12, padding: '12px 14px' }}>
                              <div style={{ width: 22, height: 22, borderRadius: '50%', backgroundColor: C.amberLight, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                                <Check size={12} color={C.amber} strokeWidth={3} />
                              </div>
                              <span style={{ fontSize: 13.5, color: C.gray700, lineHeight: 1.55 }}>{f}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </Anim>
              );
            })}
          </div>
        </div>
      </section>

      {/* ══ FEATURE COMPARISON TABLE ══ */}
      <section style={{ padding: '100px 24px', backgroundColor: C.white, position: 'relative', overflow: 'hidden' }}>
        <Orb size="440px" color="radial-gradient(circle, rgba(251,191,36,0.07), transparent)" top="-5%" left="-6%" delay={3} />
        <div style={{ maxWidth: 1100, margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <Anim delay={0}>
            <div style={{ textAlign: 'center', marginBottom: 52 }}>
              <SectionLabel text="Compare" />
              <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(28px,5vw,50px)', fontWeight: 700, color: C.gray900, marginBottom: 12 }}>
                Package Feature Comparison
              </h2>
              <p style={{ fontSize: 16.5, color: C.gray500 }}>
                Compare features from each package to choose the best one
              </p>
            </div>
          </Anim>

          <Anim delay={80}>
            <div style={{ borderRadius: 24, overflow: 'hidden', border: `2px solid ${C.gray100}`, boxShadow: '0 12px 48px rgba(0,0,0,0.08)' }}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: C.gray50 }}>
                      <th style={{ padding: '20px 24px', textAlign: 'left', fontSize: 13.5, fontWeight: 700, color: C.gray900, minWidth: 280, borderBottom: `2px solid ${C.gray200}` }}>Features</th>
                      {['Full Management', 'Marketing & Booking', 'Operational'].map(h => (
                        <th key={h} style={{ padding: '20px 24px', textAlign: 'center', fontSize: 14, fontWeight: 700, color: C.gray900, minWidth: 180, borderBottom: `2px solid ${C.gray200}` }}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {featureComparisonData.map((cat, ci) => (
                      <React.Fragment key={ci}>
                        <tr style={{ background: C.amberLight }}>
                          <td colSpan={4} style={{ padding: '12px 24px', fontSize: 11.5, fontWeight: 800, color: C.amberDark, letterSpacing: '0.12em', textTransform: 'uppercase' }}>
                            {cat.category}
                          </td>
                        </tr>
                        {cat.features.map((feat, fi) => (
                          <tr key={fi} className="tbl-row" style={{ borderBottom: `1px solid ${C.gray100}` }}>
                            <td style={{ padding: '14px 24px', fontSize: 14, fontWeight: 500, color: C.gray900 }}>{feat.name}</td>
                            {[feat.full_management, feat.marketing, feat.operational].map((has, i) => (
                              <td key={i} style={{ padding: '14px 24px', textAlign: 'center' }}>
                                <span style={{
                                  display: 'inline-flex', alignItems: 'center', gap: 5,
                                  padding: '5px 14px', borderRadius: 50, fontSize: 12.5, fontWeight: 700,
                                  backgroundColor: has ? C.green100 : C.red100,
                                  color: has ? '#166534' : C.red800,
                                }}>
                                  {has ? <Check size={12} strokeWidth={3} /> : '✗'} {has ? 'Yes' : 'No'}
                                </span>
                              </td>
                            ))}
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </Anim>
        </div>
      </section>

      {/* ══ CTA ══ */}
      <section style={{ padding: '100px 24px', backgroundColor: C.gray900, position: 'relative', overflow: 'hidden' }}>
        <Orb size="500px" color="radial-gradient(circle, rgba(217,119,6,0.22), transparent)" top="0%"  left="46%" delay={0} />
        <Orb size="300px" color="radial-gradient(circle, rgba(251,191,36,0.12), transparent)" top="55%" left="-3%" delay={3} />
        <div style={{ maxWidth: 700, margin: '0 auto', textAlign: 'center', position: 'relative', zIndex: 1 }}>
          <Anim delay={0}>
            <p style={{ color: C.amber, fontSize: 12, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', marginBottom: 20 }}>Ready to Start?</p>
            <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(32px,6vw,60px)', fontWeight: 700, color: '#fff', lineHeight: 1.1, marginBottom: 18 }}>
              Ready to Maximize Your<br /><span className="shimmer-text">Villa's Potential?</span>
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 16, marginBottom: 42, lineHeight: 1.78 }}>
              Contact us now for a free consultation and start optimizing your villa's revenue
            </p>
            <WaBtn onClick={() => handleWhatsApp()} style={{ padding: '16px 40px', fontSize: 17 }}>
              <WaIcon size={20} /> Chat WhatsApp
            </WaBtn>
          </Anim>
        </div>
      </section>


    </div>
    </MainLayout>
  );
};

export default BusinessPage;